import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const WorkOrderDashboard = ({ workOrders, equipment, technicians, onRefresh }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const filteredWorkOrders = workOrders?.filter((wo) => {
    const matchesStatus = statusFilter === 'all' || wo?.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || wo?.priority === priorityFilter;
    return matchesStatus && matchesPriority;
  });

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pending', color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
      in_progress: { label: 'In Progress', color: 'text-primary', bg: 'bg-primary/10', icon: 'Loader' },
      completed: { label: 'Completed', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
      cancelled: { label: 'Cancelled', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'XCircle' }
    };
    const cfg = config?.[status] || config?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      urgent: { label: 'Urgent', color: 'text-error', bg: 'bg-error/10' },
      high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10' },
      medium: { label: 'Medium', color: 'text-primary', bg: 'bg-primary/10' },
      low: { label: 'Low', color: 'text-muted-foreground', bg: 'bg-muted' }
    };
    const cfg = config?.[priority] || config?.medium;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const stats = {
    total: workOrders?.length || 0,
    pending: workOrders?.filter(wo => wo?.status === 'pending')?.length || 0,
    inProgress: workOrders?.filter(wo => wo?.status === 'in_progress')?.length || 0,
    completed: workOrders?.filter(wo => wo?.status === 'completed')?.length || 0
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="ClipboardList" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-foreground">{stats?.total}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Total Orders</p>
        </div>

        <div className="bg-card rounded-xl border border-warning/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <span className="text-2xl font-bold text-warning">{stats?.pending}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Pending</p>
        </div>

        <div className="bg-card rounded-xl border border-primary/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Loader" size={20} className="text-primary" />
            <span className="text-2xl font-bold text-primary">{stats?.inProgress}</span>
          </div>
          <p className="text-sm font-medium text-foreground">In Progress</p>
        </div>

        <div className="bg-card rounded-xl border border-success/20 p-4">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle2" size={20} className="text-success" />
            <span className="text-2xl font-bold text-success">{stats?.completed}</span>
          </div>
          <p className="text-sm font-medium text-foreground">Completed</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
            />
          </div>
          <div className="flex-1">
            <Select
              options={priorityOptions}
              value={priorityFilter}
              onChange={setPriorityFilter}
              placeholder="Filter by priority"
            />
          </div>
        </div>
      </div>

      {/* Work Orders List */}
      <div className="space-y-4">
        {filteredWorkOrders?.map((wo) => {
          const equipmentItem = equipment?.find(eq => eq?.id === wo?.equipment_id);
          const assignedTech = technicians?.find(tech => tech?.id === wo?.assigned_to);

          return (
            <div
              key={wo?.id}
              className="bg-card rounded-xl border border-border p-4 md:p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-foreground">{wo?.title}</h3>
                    {getStatusBadge(wo?.status)}
                    {getPriorityBadge(wo?.priority)}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{wo?.description}</p>

                  {equipmentItem && (
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p>
                        <Icon name="Package" size={14} className="inline mr-1" />
                        {equipmentItem?.name} - {equipmentItem?.category}
                      </p>
                      <p>
                        <Icon name="MapPin" size={14} className="inline mr-1" />
                        {equipmentItem?.location}
                      </p>
                    </div>
                  )}

                  {assignedTech && (
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground">
                        <Icon name="User" size={14} className="inline mr-1" />
                        Assigned to: <span className="font-medium text-foreground">{assignedTech?.name}</span>
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  {wo?.status === 'pending' && (
                    <Button variant="primary" size="sm" iconName="Play" iconPosition="left">
                      Start Work
                    </Button>
                  )}
                  {wo?.status === 'in_progress' && (
                    <Button variant="success" size="sm" iconName="CheckCircle2" iconPosition="left">
                      Complete
                    </Button>
                  )}
                  <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
                    View Details
                  </Button>
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/30 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Work Order #</p>
                  <p className="font-medium text-foreground">{wo?.work_order_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Scheduled Date</p>
                  <p className="font-medium text-foreground">
                    {wo?.scheduled_date ? new Date(wo?.scheduled_date)?.toLocaleDateString() : 'Not scheduled'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
                  <p className="font-medium text-foreground">
                    {wo?.estimated_cost ? `$${wo?.estimated_cost}` : 'TBD'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Created</p>
                  <p className="font-medium text-foreground">
                    {new Date(wo?.created_at)?.toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredWorkOrders?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="ClipboardList" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Work Orders Found</h3>
          <p className="text-muted-foreground">No work orders match the selected filters</p>
        </div>
      )}
    </div>
  );
};

export default WorkOrderDashboard;